import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './LandingPage.css';

export default function LandingPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'similar' | 'bought' | 'trending'>('similar');
  const [activeDemoTab, setActiveDemoTab] = useState<'similar' | 'bought' | 'trending'>('similar');

  const demoProducts = {
    similar: [
      { name: 'Sport Pro Sneakers', price: '$89.99', initial: 'SP' },
      { name: 'Tech Fit Shirt', price: '$34.99', initial: 'TF' },
      { name: 'Urban Backpack', price: '$59.99', initial: 'UB' },
    ],
    bought: [
      { name: 'Performance Socks (3-Pack)', price: '$18.99', initial: 'PS' },
      { name: 'Sports Water Bottle', price: '$24.99', initial: 'SW' },
      { name: 'Wireless Sport Earbuds', price: '$79.99', initial: 'WS' },
    ],
    trending: [
      { name: 'Smart Fitness Watch', price: '$199.99', initial: 'SF' },
      { name: 'Yoga Mat Premium', price: '$45.99', initial: 'YM' },
      { name: 'Resistance Bands Set', price: '$29.99', initial: 'RB' },
    ],
  };

  const showcaseProducts = {
    similar: [
      { name: 'Premium Jacket', price: '$129.99', match: '87% match', initial: 'PJ' },
      { name: 'Slim Fit Pants', price: '$79.99', match: '82% match', initial: 'SF' },
      { name: 'Wool Scarf', price: '$39.99', match: '76% match', initial: 'WS' },
    ],
    bought: [
      { name: 'Designer Tie', price: '$49.99', match: '92% bought together', initial: 'DT' },
      { name: 'Leather Shoes', price: '$159.99', match: '88% bought together', initial: 'LS' },
      { name: 'Classic Watch', price: '$199.99', match: '79% bought together', initial: 'CW' },
    ],
    trending: [
      { name: 'Noise-Canceling Headphones', price: '$249.99', match: 'Hot this week', initial: 'NC' },
      { name: 'Laptop Backpack Pro', price: '$89.99', match: 'Trending', initial: 'LB' },
      { name: 'Phone Stand Deluxe', price: '$34.99', match: 'Popular', initial: 'PS' },
    ],
  };

  const showcaseTitles = {
    similar: t('demo.showcaseTitles.similar'),
    bought: t('demo.showcaseTitles.bought'),
    trending: t('demo.showcaseTitles.trending'),
  };

  const tabLabels = {
    similar: t('demo.labels.similar'),
    bought: t('demo.labels.bought'),
    trending: t('demo.labels.trending'),
  };

  const features = [
    {
      iconClass: 'feature-icon-brain',
      title: t('features.behavioral.title'),
      description: t('features.behavioral.description'),
      highlight: t('features.behavioral.highlight')
    },
    {
      iconClass: 'feature-icon-chat',
      title: t('features.chatbot.title'),
      description: t('features.chatbot.description'),
      highlight: t('features.chatbot.highlight')
    },
    {
      iconClass: 'feature-icon-chart',
      title: t('features.analytics.title'),
      description: t('features.analytics.description'),
      highlight: t('features.analytics.highlight')
    },
    {
      iconClass: 'feature-icon-palette',
      title: t('features.customizable.title'),
      description: t('features.customizable.description'),
      highlight: t('features.customizable.highlight')
    },
    {
      iconClass: 'feature-icon-bolt',
      title: t('features.fast.title'),
      description: t('features.fast.description'),
      highlight: t('features.fast.highlight')
    },
    {
      iconClass: 'feature-icon-shield',
      title: t('features.privacy.title'),
      description: t('features.privacy.description'),
      highlight: t('features.privacy.highlight')
    },
    {
      iconClass: 'feature-icon-box',
      title: t('features.bundles.title'),
      description: t('features.bundles.description'),
      highlight: t('features.bundles.highlight')
    },
    {
      iconClass: 'feature-icon-refresh',
      title: t('features.types.title'),
      description: t('features.types.description'),
      highlight: t('features.types.highlight')
    }
  ];

  const comparisonPoints = [
    {
      shopify: t('comparison.points.1.shopify'),
      ours: t('comparison.points.1.ours'),
    },
    {
      shopify: t('comparison.points.2.shopify'),
      ours: t('comparison.points.2.ours'),
    },
    {
      shopify: t('comparison.points.3.shopify'),
      ours: t('comparison.points.3.ours'),
    },
    {
      shopify: t('comparison.points.4.shopify'),
      ours: t('comparison.points.4.ours'),
    },
    {
      shopify: t('comparison.points.5.shopify'),
      ours: t('comparison.points.5.ours'),
    },
    {
      shopify: t('comparison.points.6.shopify'),
      ours: t('comparison.points.6.ours'),
    },
    {
      shopify: t('comparison.points.7.shopify'),
      ours: t('comparison.points.7.ours'),
    },
    {
      shopify: t('comparison.points.8.shopify'),
      ours: t('comparison.points.8.ours'),
    }
  ];

  const pricingPlans = [
    {
      name: t('pricing.starter.name'),
      price: t('pricing.starter.price'),
      period: t('pricing.starter.period'),
      description: t('pricing.starter.description'),
      limit: t('pricing.starter.limit'),
      features: [
        t('pricing.starter.features.orders'),
        t('pricing.starter.features.products'),
        t('pricing.starter.features.recommendations'),
        t('pricing.starter.features.widget'),
        t('pricing.starter.features.analytics'),
        t('pricing.starter.features.support')
      ],
      cta: t('pricing.starter.cta'),
      highlighted: false
    },
    {
      name: t('pricing.growth.name'),
      price: t('pricing.growth.price'),
      period: t('pricing.growth.period'),
      description: t('pricing.growth.description'),
      limit: t('pricing.growth.limit'),
      features: [
        t('pricing.growth.features.orders'),
        t('pricing.growth.features.everything'),
        t('pricing.growth.features.bundles'),
        t('pricing.growth.features.bought'),
        t('pricing.growth.features.analytics'),
        t('pricing.growth.features.support'),
        t('pricing.growth.features.chat')
      ],
      cta: t('pricing.growth.cta'),
      highlighted: false
    },
    {
      name: t('pricing.professional.name'),
      price: t('pricing.professional.price'),
      period: t('pricing.professional.period'),
      description: t('pricing.professional.description'),
      limit: t('pricing.professional.limit'),
      features: [
        t('pricing.professional.features.orders'),
        t('pricing.professional.features.everything'),
        t('pricing.professional.features.email'),
        t('pricing.professional.features.testing'),
        t('pricing.professional.features.api'),
        t('pricing.professional.features.support'),
        t('pricing.professional.features.training')
      ],
      cta: t('pricing.professional.cta'),
      highlighted: true,
      badge: t('pricing.professional.badge')
    },
    {
      name: t('pricing.enterprise.name'),
      price: t('pricing.enterprise.price'),
      period: t('pricing.enterprise.period'),
      description: t('pricing.enterprise.description'),
      limit: t('pricing.enterprise.limit'),
      features: [
        t('pricing.enterprise.features.orders'),
        t('pricing.enterprise.features.everything'),
        t('pricing.enterprise.features.manager'),
        t('pricing.enterprise.features.sla'),
        t('pricing.enterprise.features.integration'),
        t('pricing.enterprise.features.billing'),
        t('pricing.enterprise.features.custom')
      ],
      cta: t('pricing.enterprise.cta'),
      highlighted: false
    }
  ];

  const howItWorks = [
    {
      step: 1,
      title: t('howItWorks.step1.title'),
      description: t('howItWorks.step1.description'),
    },
    {
      step: 2,
      title: t('howItWorks.step2.title'),
      description: t('howItWorks.step2.description'),
    },
    {
      step: 3,
      title: t('howItWorks.step3.title'),
      description: t('howItWorks.step3.description'),
    },
    {
      step: 4,
      title: t('howItWorks.step4.title'),
      description: t('howItWorks.step4.description'),
    }
  ];

  const integrations = [
    { name: 'Shopify' },
    { name: 'Shopify POS' },
    { name: 'Shopify Markets' },
    { name: 'Online Store 2.0' }
  ];

  const faqs = [
    {
      question: t('faq.q1.question'),
      answer: t('faq.q1.answer')
    },
    {
      question: t('faq.q2.question'),
      answer: t('faq.q2.answer')
    },
    {
      question: t('faq.q3.question'),
      answer: t('faq.q3.answer')
    },
    {
      question: t('faq.q4.question'),
      answer: t('faq.q4.answer')
    },
    {
      question: t('faq.q5.question'),
      answer: t('faq.q5.answer')
    },
    {
      question: t('faq.q6.question'),
      answer: t('faq.q6.answer')
    },
    {
      question: t('faq.q7.question'),
      answer: t('faq.q7.answer')
    },
    {
      question: t('faq.q8.question'),
      answer: t('faq.q8.answer')
    }
  ];

  const technologyItems = [
    { key: 'mf', iconClass: 'tech-icon-brain' },
    { key: 'lightgbm', iconClass: 'tech-icon-chart' },
    { key: 'bandit', iconClass: 'tech-icon-target' },
    { key: 'crossShop', iconClass: 'tech-icon-globe' },
    { key: 'cache', iconClass: 'tech-icon-bolt' },
  ];

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="navbar">
        <div className="navbar-container">
          <div className="navbar-logo">
            <img src="/icons/app-icon.svg" alt="Nexus AI" className="logo-img" />
            <span className="logo-text">{t('app.name')}</span>
          </div>
          <div className="navbar-links">
            <a href="#features">{t('nav.features')}</a>
            <a href="#comparison">{t('nav.whyUs')}</a>
            <a href="#how-it-works">{t('nav.howItWorks')}</a>
            <a href="#pricing">{t('nav.pricing')}</a>
            <a href="#faq">{t('nav.faq')}</a>
          </div>
          <div className="navbar-cta">
            <a 
              href="https://apps.shopify.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-primary"
            >
              {t('nav.installApp')}
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-background">
          <div className="hero-gradient"></div>
          <div className="hero-particles"></div>
        </div>
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-badge">
              <span>{t('hero.badge')}</span>
            </div>
            <h1 className="hero-title">
              {t('hero.title')}
              <span className="gradient-text"> {t('hero.titleHighlight')}</span>
            </h1>
            <p className="hero-subtitle">
              {t('hero.subtitle')}
            </p>
            <div className="hero-cta">
              <div className="hero-buttons">
                <a 
                  href="https://apps.shopify.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-primary btn-large"
                >
                  {t('hero.installFromStore')}
                </a>
                <a 
                  href="/docs" 
                  className="btn-secondary btn-large"
                >
                  {t('hero.viewDocumentation')}
                </a>
              </div>
              <div className="hero-trust">
                <span>✓ {t('hero.trust.trial')}</span>
                <span>✓ {t('hero.trust.noCard')}</span>
                <span>✓ {t('hero.trust.cancel')}</span>
                <span>✓ {t('hero.trust.shopifyPartner')}</span>
              </div>
            </div>
          </div>
          <div className="hero-visual">
            <div className="hero-mockup">
              <div className="mockup-browser">
                <div className="browser-header">
                  <div className="browser-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <div className="browser-url">your-store.myshopify.com</div>
                </div>
                <div className="browser-content">
                  <div className="widget-preview">
                    <div className="demo-tabs">
                      <button 
                        className={`demo-tab ${activeTab === 'similar' ? 'active' : ''}`}
                        onClick={() => setActiveTab('similar')}
                      >
                        {t('demo.tabs.similar')}
                      </button>
                      <button 
                        className={`demo-tab ${activeTab === 'bought' ? 'active' : ''}`}
                        onClick={() => setActiveTab('bought')}
                      >
                        {t('demo.tabs.bought')}
                      </button>
                      <button 
                        className={`demo-tab ${activeTab === 'trending' ? 'active' : ''}`}
                        onClick={() => setActiveTab('trending')}
                      >
                        {t('demo.tabs.trending')}
                      </button>
                    </div>
                    <h3>{tabLabels[activeTab]}</h3>
                    <div className="preview-products">
                      {demoProducts[activeTab].map((product, index) => (
                        <div key={index} className="preview-product">
                          <div className="product-image product-placeholder">
                            <span className="product-initial">{product.initial}</span>
                          </div>
                          <div className="product-info">
                            <span className="product-name">{product.name}</span>
                            <span className="product-price">{product.price}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="widget-badge">{t('demo.poweredBy')}</div>
                  </div>
                </div>
              </div>
              <div className="mockup-stats">
                <div className="stat-bubble stat-1">
                  <span className="stat-value">AI</span>
                  <span className="stat-label">{t('stats.aiPowered')}</span>
                </div>
                <div className="stat-bubble stat-2">
                  <span className="stat-value">&lt;100ms</span>
                  <span className="stat-label">{t('stats.response')}</span>
                </div>
                <div className="stat-bubble stat-3">
                  <span className="stat-value">GDPR</span>
                  <span className="stat-label">{t('stats.gdpr')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="logos-section">
        <div className="logos-container">
          <p className="logos-title">{t('logos.title')}</p>
          <div className="logos-grid">
            <div className="logo-item"><span className="trust-icon trust-shopify"></span> <span>{t('logos.shopify')}</span></div>
            <div className="logo-item"><span className="trust-icon trust-shield"></span> <span>{t('logos.gdpr')}</span></div>
            <div className="logo-item"><span className="trust-icon trust-bolt"></span> <span>{t('logos.response')}</span></div>
            <div className="logo-item"><span className="trust-icon trust-brain"></span> <span>{t('logos.ml')}</span></div>
          </div>
        </div>
      </section>

      {/* Comparison Section - NEW */}
      <section id="comparison" className="comparison-section">
        <div className="comparison-container">
          <div className="section-header">
            <span className="section-badge">{t('comparison.badge')}</span>
            <h2 className="section-title">{t('comparison.title')}</h2>
            <p className="section-subtitle">
              {t('comparison.subtitle')}
            </p>
          </div>
          <div className="comparison-table">
            <div className="comparison-header">
              <div className="comparison-col shopify-col">
                <span>{t('comparison.shopify')}</span>
              </div>
              <div className="comparison-col our-col">
                <img src="/icons/app-icon.svg" alt="" className="col-logo" />
                <span>{t('comparison.ours')}</span>
              </div>
            </div>
            {comparisonPoints.map((point, index) => (
              <div key={index} className="comparison-row">
                <div className="comparison-cell shopify-cell" data-label={t('comparison.shopify')}>
                  <span className="cell-indicator negative">✕</span>
                  {point.shopify}
                </div>
                <div className="comparison-cell our-cell" data-label={t('comparison.ours')}>
                  <span className="cell-indicator positive">✓</span>
                  {point.ours}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="features-container">
          <div className="section-header">
            <span className="section-badge">{t('features.badge')}</span>
            <h2 className="section-title">{t('features.title')}</h2>
            <p className="section-subtitle">
              {t('features.subtitle')}
            </p>
          </div>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className={`feature-icon ${feature.iconClass}`}></div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
                <div className="feature-highlight">{feature.highlight}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Technology Section */}
      <section className="technology-section">
        <div className="technology-container">
          <div className="section-header">
            <span className="section-badge">{t('technology.badge')}</span>
            <h2 className="section-title">{t('technology.title')}</h2>
            <p className="section-subtitle">
              {t('technology.subtitle')}
            </p>
          </div>
          <div className="technology-grid">
            {technologyItems.map((item) => (
              <div key={item.key} className="technology-card">
                <div className={`technology-icon ${item.iconClass}`}></div>
                <div className="technology-tag">{t(`technology.${item.key}.tag`)}</div>
                <h3 className="technology-title">{t(`technology.${item.key}.title`)}</h3>
                <p className="technology-description">{t(`technology.${item.key}.description`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="how-it-works-section">
        <div className="how-it-works-container">
          <div className="section-header">
            <span className="section-badge">{t('howItWorks.badge')}</span>
            <h2 className="section-title">{t('howItWorks.title')}</h2>
            <p className="section-subtitle">
              {t('howItWorks.subtitle')}
            </p>
          </div>
          <div className="steps-grid">
            {howItWorks.map((item) => (
              <div key={item.step} className="step-card">
                <div className="step-number">{item.step}</div>
                <h3 className="step-title">{item.title}</h3>
                <p className="step-description">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Demo Section */}
      <section className="demo-section">
        <div className="demo-container">
          <div className="demo-content">
            <span className="section-badge">{t('demo.livePreview', 'Live Preview')}</span>
            <h2 className="demo-title">{t('demo.title')}</h2>
            <p className="demo-description">
              {t('demo.subtitle')}
            </p>
            <ul className="demo-features">
              <li>✓ {t('demo.features.realtime')}</li>
              <li>✓ {t('demo.features.similar')}</li>
              <li>✓ {t('demo.features.bought')}</li>
              <li>✓ {t('demo.features.trending')}</li>
              <li>✓ {t('demo.features.email')}</li>
            </ul>
            <a 
              href="https://apps.shopify.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-primary btn-large"
            >
              {t('demo.tryIt')}
            </a>
          </div>
          <div className="demo-visual">
            <div className="demo-showcase">
              <div 
                className={`showcase-tab ${activeDemoTab === 'similar' ? 'active' : ''}`}
                onClick={() => setActiveDemoTab('similar')}
              >
                {t('demo.tabs.similar')}
              </div>
              <div 
                className={`showcase-tab ${activeDemoTab === 'bought' ? 'active' : ''}`}
                onClick={() => setActiveDemoTab('bought')}
              >
                {t('demo.tabs.bought')}
              </div>
              <div 
                className={`showcase-tab ${activeDemoTab === 'trending' ? 'active' : ''}`}
                onClick={() => setActiveDemoTab('trending')}
              >
                {t('demo.tabs.trending')}
              </div>
            </div>
            <div className="demo-widget">
              <div className="widget-header">
                <h4>{showcaseTitles[activeDemoTab]}</h4>
              </div>
              <div className="widget-products">
                {showcaseProducts[activeDemoTab].map((product, index) => (
                  <div key={index} className="widget-product">
                    <div className="wp-image product-placeholder">
                      <span className="product-initial">{product.initial}</span>
                    </div>
                    <div className="wp-details">
                      <span className="wp-name">{product.name}</span>
                      <span className="wp-price">{product.price}</span>
                      <span className="wp-badge">{product.match}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing-section">
        <div className="pricing-container">
          <div className="section-header">
            <span className="section-badge">{t('pricing.badge')}</span>
            <h2 className="section-title">{t('pricing.title')}</h2>
            <p className="section-subtitle">
              {t('pricing.subtitle')}
            </p>
          </div>
          <div className="pricing-grid">
            {pricingPlans.map((plan, index) => (
              <div key={index} className={`pricing-card ${plan.highlighted ? 'highlighted' : ''}`}>
                {plan.badge && <div className="pricing-badge">{plan.badge}</div>}
                <div className="pricing-header">
                  <h3 className="pricing-name">{plan.name}</h3>
                  <p className="pricing-description">{plan.description}</p>
                </div>
                <div className="pricing-price">
                  <span className="price-value">{plan.price}</span>
                  <span className="price-period">{plan.period}</span>
                </div>
                {plan.limit && <div className="pricing-limit">{plan.limit}</div>}
                <ul className="pricing-features">
                  {plan.features.map((feature, fIndex) => (
                    <li key={fIndex}>
                      <span className="check-icon">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
                {index === 3 ? (
                  <a 
                    href="mailto:support@shopicraft-solutions.com"
                    className="btn-secondary btn-full"
                  >
                    {plan.cta}
                  </a>
                ) : (
                  <a 
                    href="https://apps.shopify.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={plan.highlighted ? 'btn-primary btn-full' : 'btn-secondary btn-full'}
                  >
                    {plan.cta}
                  </a>
                )}
              </div>
            ))}
          </div>
          <div className="pricing-guarantee">
            <span>{t('pricing.guarantee')}</span>
          </div>
        </div>
      </section>

      {/* Integrations Section */}
      <section className="integrations-section">
        <div className="integrations-container">
          <div className="section-header">
            <span className="section-badge">{t('integrations.badge')}</span>
            <h2 className="section-title">{t('integrations.title')}</h2>
          </div>
          <div className="integrations-grid">
            {integrations.map((integration, index) => (
              <div key={index} className="integration-card">
                <span className="integration-name">{integration.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Languages Section */}
      <section className="languages-section">
        <div className="languages-container">
          <div className="section-header">
            <span className="section-badge">{t('languages.badge')}</span>
            <h2 className="section-title">{t('languages.title')}</h2>
            <p className="section-subtitle">
              {t('languages.subtitle')}
            </p>
          </div>
          <div className="languages-grid">
            <div className="language-item">�🇩 Català</div>
            <div className="language-item">�🇨🇿 Čeština</div>
            <div className="language-item">🇩🇰 Dansk</div>
            <div className="language-item">🇩🇪 Deutsch</div>
            <div className="language-item">🇬🇧 English</div>
            <div className="language-item">🇪🇸 Español</div>
            <div className="language-item">🇫🇷 Français</div>
            <div className="language-item">🇮🇹 Italiano</div>
            <div className="language-item">🇳🇱 Nederlands</div>
            <div className="language-item">🇳🇴 Norsk</div>
            <div className="language-item">🇵🇱 Polski</div>
            <div className="language-item">🇧🇷 Português (BR)</div>
            <div className="language-item">🇵🇹 Português (PT)</div>
            <div className="language-item">🇫🇮 Suomi</div>
            <div className="language-item">🇸🇪 Svenska</div>
            <div className="language-item">🇹🇷 Türkçe</div>
            <div className="language-item">🇷🇺 Русский</div>
            <div className="language-item">��🇭 ภาษาไทย</div>
            <div className="language-item">🇯🇵 日本語</div>
            <div className="language-item">🇨🇳 简体中文</div>
            <div className="language-item">🇹🇼 繁體中文</div>
            <div className="language-item">🇰🇷 한국어</div>
          </div>
          <div className="languages-note">
            <span>{t('languages.count')}</span>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="faq-section">
        <div className="faq-container">
          <div className="section-header">
            <span className="section-badge">{t('faq.badge')}</span>
            <h2 className="section-title">{t('faq.title')}</h2>
          </div>
          <div className="faq-grid">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-item">
                <h3 className="faq-question">{faq.question}</h3>
                <p className="faq-answer">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta-section">
        <div className="final-cta-container">
          <h2 className="final-cta-title">
            {t('cta.title')}
          </h2>
          <p className="final-cta-subtitle">
            {t('cta.subtitle')}
          </p>
          <div className="final-cta-actions">
            <a 
              href="https://apps.shopify.com"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary btn-large"
            >
              {t('cta.button')}
            </a>
          </div>
          <div className="final-cta-trust">
            <span>✓ {t('hero.trust.shopifyPartner')}</span>
            <span>✓ {t('cta.trust.setup')}</span>
            <span>✓ {t('cta.trust.support')}</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-main">
            <div className="footer-brand">
              <div className="footer-logo">
                <img src="/icons/app-icon.svg" alt="Nexus AI" className="logo-img" />
                <span className="logo-text">{t('app.name')}</span>
              </div>
              <p className="footer-description">
                {t('footer.description')}
              </p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4>{t('footer.product')}</h4>
                <a href="#features">{t('footer.features')}</a>
                <a href="#pricing">{t('footer.pricing')}</a>
                <a href="#how-it-works">{t('footer.howItWorks')}</a>
                <a href="/docs">{t('footer.documentation')}</a>
              </div>
              <div className="footer-column">
                <h4>{t('footer.company')}</h4>
                <a href="/about">{t('footer.about')}</a>
                <a href="mailto:support@shopicraft-solutions.com">{t('footer.contact')}</a>
              </div>
              <div className="footer-column">
                <h4>{t('footer.legal')}</h4>
                <a href="/privacy">{t('footer.privacy')}</a>
                <a href="/terms">{t('footer.terms')}</a>
                <a href="/gdpr">{t('footer.gdpr')}</a>
                <a href="/cookies">{t('footer.cookies')}</a>
              </div>
              <div className="footer-column">
                <h4>{t('footer.support')}</h4>
                <a href="/docs">{t('footer.documentation')}</a>
                <a href="mailto:support@shopicraft-solutions.com">{t('footer.email')}</a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>{t('footer.copyright')}</p>
            <div className="footer-social">
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
